<template lang="pug">
  div
    #js-payment-request-button
    vue-premium-join-confirm-modal(ref='premiumJoinConfirmModal', lang='en')
    vue-modal(name='modal-error', :adaptive="true", width='95%', height='auto')
      .v-error-modal
        .u-align-center
          .h3.u-weight-bold Error
          .u-mts
            p {{ errorMessage }}
          .u-mtm
            button.c-btn.c-btn--round.c-btn--alert.c-btn--inverse.c-btn--lg(type='button', @click='$modal.hide("modal-error")') Close
</template>

<script>
import ProductSku from '../../../stores/schema/ProductSku';
import { Payment } from '../../../stores/schema/Payment';
import { LANG_CODES } from '../../../constants/app';
import { I18n } from '../../../util/mixin/i18n.mixin';
import merge from 'latest_modules/lodash/merge';
import premiumJoinConfirmModalComponent from '../../premium/joinConfirmModal.vue';

/*
* モバイル決済ボタン (Apple Pay、Google Pay)
*/
export default {
  name: 'mobilePay',

  mixins: [I18n],

  components: {
    'vue-premium-join-confirm-modal': premiumJoinConfirmModalComponent
  },

  props: {
    stripeKey: {
      type: String,
      required: true
    },

    possiblePremiumPlan: {
      type: Object
    },

    possiblePremiumPlanType: {
      type: String
    },

    channelId: {
      type: String
    }
  },

  data() {
    return {
      paymentRequest: null,
      button: null,
      shipping: null,
      p: null,
      payment: null,
      premium: null,
      initialUpdateDetails: null,
      shippingOptions: [],
      shippingCharges: null,
      selectedShippingOption: null,
      pending: true,
      errorMessage: null
    };
  },

  watch: {
    selectedSku() {
      return this.reset();
    }
  },

  computed: {
    product() {
      return this.$store.state.product;
    },

    selectedSku() {
      return this.$store.getters['product/skus/selectedSku'];
    },

    user() {
      return this.$store.state.user;
    },

    isJoiningPremium() {
      return !this.user.is_premium_member && this.user.will_join_premium;
    },

    isPremiumMemberJoiningFirstTime() {
      return this.isJoiningPremium && !this.user.had_registered_premium_in_past;
    },

    canGetBonus() {
      const hasPaymentAndPremium = this.payment && this.premium;
      const canGetBonus =
        !hasPaymentAndPremium ||
        (this.premium.can_get_bonus && !this.payment.already_used_for_premium);
      return !this.isPremiumMemberJoiningFirstTime || canGetBonus;
    },

    skuSellingPrice() {
      return ProductSku.getSellingPrice(this.selectedSku, this.user);
    },

    skuRegularPrice() {
      return ProductSku.getRegularPrice(this.selectedSku, this.user);
    },

    skuLabel() {
      switch (this.user.lang) {
        case LANG_CODES.JA:
          return this.selectedSku.titles.ja_jp;
        default:
          return this.selectedSku.titles.en_us;
      }
    },

    shippingOptionPrice() {
      return (
        ((this.selectedShippingOption && this.selectedShippingOption.amount) ||
          0) / 100
      );
    },

    shippingImportFees() {
      return (
        (this.shippingCharges &&
          this.selectedShippingOption &&
          this.shippingCharges[this.selectedShippingOption.id] &&
          this.shippingCharges[this.selectedShippingOption.id].import_fees) ||
        0
      );
    },

    premiumPlan() {
      return (
        (this.premium &&
          this.premium.plan &&
          this.premium.plan.type &&
          this.premium.plan.type[this.premiumPlanType]) ||
        this.possiblePremiumPlan
      );
    },

    premiumPlanType() {
      return (
        (this.premium && this.premium.plan_type) || this.possiblePremiumPlanType
      );
    },

    premiumPlanPrice() {
      if (!this.user.will_join_premium) {
        return 0;
      }
      return (
        (this.premiumPlan &&
          this.premiumPlan.price &&
          this.premiumPlan.price.value) ||
        0
      );
    },

    // This is used by joinConfirmModal
    premiumPlanCurrency() {
      return (
        this.premiumPlan &&
        this.premiumPlan.price &&
        this.premiumPlan.price.currency
      );
    },

    // This is used by joinConfirmModal
    premiumJoiningSavePrice() {
      return (
        this.premiumPlanDiscount + this.skuRegularPrice - this.skuSellingPrice
      );
    },

    premiumPlanLabel() {
      return `TOM PREMIUM MEMBERSHIP (${this.premiumPlan.term} days)`;
    },

    premiumPlanDiscount() {
      if (!this.user.will_join_premium) {
        return 0;
      }
      return (this.premiumPlan && this.premiumPlan.point / 100) || 0;
    },

    premiumPlanDiscountLabel() {
      if (this.premiumPlanType === 'recontract') {
        return 'Welcome Back Discount';
      }
      return 'Welcome Discount';
    },

    total() {
      const total =
        this.skuSellingPrice +
        this.shippingOptionPrice +
        this.shippingImportFees +
        this.premiumPlanPrice -
        this.premiumPlanDiscount;
      return Math.max(total, 0);
    },

    updateDetails() {
      const details = {
        total: {
          amount: this.toStripeSubunit(this.total),
          label: 'Grand Total',
          pending: this.pending
        },
        displayItems: [
          {
            amount: this.toStripeSubunit(this.skuSellingPrice),
            label: this.skuLabel || ' ', // label should be a string.
            pending: false
          }
        ]
      };
      if (this.isJoiningPremium) {
        details.displayItems.push({
          amount: this.toStripeSubunit(this.premiumPlanPrice),
          label: this.premiumPlanLabel || ' ',
          pending: false
        });
        if (this.premiumPlanDiscount) {
          details.displayItems.push({
            amount: -this.toStripeSubunit(this.premiumPlanDiscount),
            label: this.premiumPlanDiscountLabel,
            pending: false
          });
        }
      }
      if (!this.selectedSku.is_digital_content) {
        details.displayItems.push({
          amount: this.toStripeSubunit(this.shippingOptionPrice),
          label: 'Estimated Shipping',
          pending: false
        });
      }
      if (this.shippingImportFees) {
        details.displayItems.push({
          amount: this.toStripeSubunit(this.shippingImportFees),
          label: 'Import Fees',
          pending: false
        });
      }
      return details;
    }
  },

  methods: {
    reset() {
      if (this.button) {
        this.button.destroy();
      }
      const stripe = window.Stripe(this.stripeKey, {
        apiVersion: '2024-06-20'
      });
      if (!this.initialUpdateDetails) {
        this.initialUpdateDetails = this.updateDetails;
      }
      this.paymentRequest = stripe.paymentRequest(
        merge(
          {
            country: 'US',
            currency: 'usd',
            requestPayerName: true, // This is for getting billing address for Apple Pay
            requestShipping: !this.selectedSku.is_digital_content
          },
          this.initialUpdateDetails
        )
      );
      return this.paymentRequest.canMakePayment().then((result) => {
        if (!result) {
          return;
        }
        this.button = stripe.elements().create('paymentRequestButton', {
          paymentRequest: this.paymentRequest
        });
        this.button.on('click', this.onClick);
        this.button.mount('#js-payment-request-button');
        this.paymentRequest.on(
          'shippingaddresschange',
          this.onShippingAddressChange
        );
        this.paymentRequest.on(
          'shippingoptionchange',
          this.onShippingOptionChange
        );
        this.paymentRequest.on('token', this.onPayment);
        return this.paymentRequest.on('cancel', this.onCancel);
      });
    },

    onClick(ev) {
      if (window.tom && window.tom.helper && window.tom.helper.ga) {
        window.tom.helper.ga.push(['event', 'EC', 'Clicked', 'Mobile Pay', 1]);
      }

      if (!this.isJoiningPremium) {
        return this._onClick(ev);
      }
      ev.preventDefault();
      return this.$refs.premiumJoinConfirmModal.open({
        continueButtonLabels: {
          en: 'Continue',
          ja: '次へ'
        },
        handler: () => {
          this._onClick(ev);
          this.$refs.premiumJoinConfirmModal.close();
          return this.paymentRequest.show();
        },
        closeHandler: () => {
          return this.onCancel();
        }
      });
    },

    _onClick() {
      this.paymentRequest.update(this.initialUpdateDetails);
      this.p = this.clearCart();
      this.p = this.p.then(() => {
        return this.addToCart({
          ga: {
            action: 'Add to Cart',
            impression: {
              id: this.selectedSku._id,
              name: this.selectedSku.titles.en_us,
              category:
                this.product.category != null
                  ? this.product.category.join('/')
                  : undefined,
              price: this.skuSellingPrice,
              quantity: 1
            }
          }
        });
      });
      this.p = this.p.then(() => {
        return this.sendGACheckout();
      });
      return (this.p = this.p.catch(this.onError));
    },

    onShippingAddressChange(ev) {
      const { shippingAddress } = ev;
      if (
        this.selectedSku.shipped_denied_country_map[shippingAddress.country]
      ) {
        const countryName =
          this.selectedSku.shipped_denied_country_map[shippingAddress.country];
        (console.error || console.log)(
          `This product cannot be shipped to ${countryName} due to copyright and other region restritions.`
        );
        // UX的にエラー表示までの時間に少しwaitを入れる
        setTimeout(
          () =>
            ev.updateWith({
              status: 'fail'
            }),
          1000
        );
        return;
      }
      this.p = this.p.then(() => {
        return this.fetchSelectableShippingMethods(shippingAddress);
      });
      this.p = this.p.then((result) => {
        const shipmentId = Object.keys(result.shippingChargesMap)[0];
        this.shippingCharges = result.shippingChargesMap[shipmentId];
        const { SHIPPING_METHODS } = result;
        const shippingMethodsByValues = Object.keys(SHIPPING_METHODS).reduce(
          function (result, k) {
            const v = SHIPPING_METHODS[k];
            result[v.value] = v;
            return result;
          },
          {}
        );
        this.shippingOptions = result.selectable_shipping_methods
          .sort(function (a, b) {
            if (a === result.shipping_method) {
              return -1;
            }
            if (b === result.shipping_method) {
              return 1;
            }
            return 0;
          })
          .map((method) => {
            const methodInfo = shippingMethodsByValues[method];
            return {
              id: methodInfo.value,
              label: this.__(method),
              detail: methodInfo.average_delivery_time,
              amount: this.toStripeSubunit(this.shippingCharges[method].billing)
            };
          });
        this.selectedShippingOption = this.shippingOptions[0];
        this.pending = false;
        return ev.updateWith(
          merge({}, this.updateDetails, {
            status: 'success',
            shippingOptions: this.shippingOptions
          })
        );
      });
      return (this.p = this.p.catch((err) => {
        let status;
        if (err.general) {
          status = 'fail';
        } else {
          status = 'invalid_shipping_address';
        }
        ev.updateWith({
          status
        });
        return this.onError(err);
      }));
    },

    onShippingOptionChange(ev) {
      const { shippingOption } = ev;
      this.selectedShippingOption = shippingOption;
      this.pending = false;
      return ev.updateWith(
        merge({}, this.updateDetails, {
          status: 'success'
        })
      );
    },

    onPayment(ev) {
      const { shippingAddress, shippingOption } = ev;
      const token = ev.token.id;
      this.p = this.p.then(() => {
        return Promise.all([
          this.clearCart(),
          this.$store
            .dispatch('payment/add', {
              method: Payment.PAYMENT_METHOD.MOBILE,
              token
            })
            .then((res) => {
              const { payments } = res;
              this.payment = payments[0].payment;
              return this.$store.dispatch('payment/changeCurrent', {
                _id: this.payment._id
              });
            })
        ]);
      });
      this.p = this.p.then(() => {
        return this.addToCart();
      });
      if (shippingAddress) {
        this.p = this.p.then(() => {
          return this.updateShippingAddress(shippingAddress);
        });
        this.p = this.p.then((result) => {
          const shipmentId = Object.keys(result.shippingChargesMap)[0];
          return this.$store.dispatch('cart/changeShippingMethod', {
            shipmentId,
            value: shippingOption.id
          });
        });
      }
      this.p = this.p.then(() => {
        if (this.canGetBonus) {
          return Promise.resolve();
        }
        return new Promise((resolve) => {
          // 決済に使うカードが既にPremium会員の決済に使われていた場合、recontractになるというmodalを出して確認する。
          ev.complete('success');
          return this.$refs.premiumJoinConfirmModal.open({
            handler() {
              return resolve();
            },
            closeHandler: () => {
              return this.onCancel();
            }
          });
        });
      });
      this.p = this.p.then(() => {
        if (!this.isJoiningPremium) {
          return Promise.resolve();
        }
        return this.$store.dispatch('premium/join', {
          channelId: this.channelId,
          planSlug: this.premium.plan.unique_slug
        });
      });
      this.p = this.p.then(() => {
        return this.$store.dispatch('cart/checkout');
      });
      this.p = this.p.then((res) => {
        window.location.href = res.redirect;
        if (this.canGetBonus) {
          return ev.complete('success');
        }
      });
      return (this.p = this.p.catch((err) => {
        if (this.canGetBonus) {
          ev.complete('fail');
        }
        return this.onError(err);
      }));
    },

    onCancel() {
      this.clearCart().catch((err) =>
        (console.error || console.log)((err.general && err.general.msg) || err)
      );
      this.selectedShippingOption = this.shippingOptions[0];
      this.reset();
      return this.$refs.premiumJoinConfirmModal.close();
    },

    onError(err) {
      this.onCancel();
      this.errorMessage =
        (err.errors && err.errors.general && err.errors.general.msg) ||
        (err.general && err.general.msg) ||
        (err.tel && err.tel.msg) ||
        err.message ||
        err;
      return this.$modal.show('modal-error');
    },

    addToCart(options) {
      if (options == null) {
        options = {};
      }
      const p = Promise.all([
        this._addToCart(options),
        this.joinPremium().then((result) => {
          this.premium = result && result.premium;
          return Promise.resolve();
        })
      ]);
      return p;
    },

    sendGACheckout() {
      if (
        !(
          window.tom &&
          window.tom.helper &&
          window.tom.helper.ga &&
          window.tom.helper.ga.checkout
        )
      ) {
        return;
      }
      const p = this.$store.dispatch('cart/check');
      return p.then(function (res) {
        const addProductList = [];
        for (let shipment of Array.from(res.cart.shipments)) {
          for (let item of Array.from(shipment.items)) {
            const addProductOptions = {
              id: item.sku.id,
              name: item.sku.full_title,
              variant: item.sku.short_title || item.sku.full_title,
              price: item.sku.sale_price || item.sku.price,
              quantity: item.qty
            };
            if (item.sku.brand) {
              addProductOptions.brand = `${
                item.sku.brand._id || item.sku.brand
              }`;
            }
            if (item.product.category) {
              addProductOptions.category = item.product.category.join('/');
            }
            addProductList.push(addProductOptions);
          }
        }
        if (!addProductList.length) {
          return;
        }
        return tom.helper.ga.checkout(
          addProductList,
          {},
          {
            currencyCode: 'USD'
          }
        );
      });
    },

    updateShippingAddress(shippingAddress) {
      if (!shippingAddress.city) {
        const lines = [].concat(
          ...Array.from(
            shippingAddress.addressLine.map((line) => line.split(' ')) || []
          )
        );
        shippingAddress.city = lines[0];
        shippingAddress.addressLine = [
          lines[1],
          lines.slice(2).join(' ')
        ].filter((line) => line);
      }
      let p = this.$store.dispatch('shipping/add', {
        name: shippingAddress.recipient,
        country: shippingAddress.country,
        zip: shippingAddress.postalCode,
        state: shippingAddress.region,
        city: shippingAddress.city,
        line1: shippingAddress.addressLine[0],
        line2: [shippingAddress.addressLine[1], shippingAddress.organization]
          .filter((v) => v)
          .join(' '),
        tel: shippingAddress.phone,
        user: this.user._id,
        temporary: true
      });
      p = p.then((shipping) => {
        return this.$store.dispatch('shipping/changeCurrent', {
          _id: shipping._id
        });
      });
      p = p.then((shipping) => {
        this.shipping = shipping;
        return this.$store.dispatch('cart/fetchSelectableShippingMethods', {
          shippingId: shipping._id
        });
      });
      return p;
    },

    fetchSelectableShippingMethods(shippingAddress) {
      return this.$store.dispatch('cart/fetchSelectableShippingMethods', {
        shippingAddress: {
          country: shippingAddress.country,
          zip: shippingAddress.postalCode,
          state: shippingAddress.region,
          city: shippingAddress.city
        }
      });
    },

    _addToCart(options) {
      if (options == null) {
        options = {};
      }
      return this.$store.dispatch('cart/addToCart', {
        product_id: this.product._id,
        sku_id: this.selectedSku._id,
        ga: options.ga
      });
    },

    clearCart() {
      return this.$store.dispatch('cart/clearCart');
    },

    joinPremium() {
      if (this.user.is_premium_member) {
        return Promise.resolve();
      }
      return this.$store.dispatch('cart/joinPremium', {
        join: this.user.will_join_premium
      });
    },

    // amount(dollars)をStripeのsubunitであるcentsに直す
    toStripeSubunit(amount) {
      return Math.round(amount * 100);
    }
  }
};
</script>

<style scoped>
.v-error-modal {
  padding: 30px 20px;
}
</style>
